import { pb } from './pocketbase';

/**
 * Service pour gérer les articles via PocketBase
 */
export class ArticlesService {
  static collectionName = 'articles';

  /**
   * Récupérer tous les articles avec leurs catégories
   * @param {Object} options - Options de requête (page, perPage, sort, filter)
   * @returns {Promise<Object>} Liste paginée des articles
   */
  static async getAll(options = {}) {
    try {
      const {
        page = 1,
        perPage = 20,
        sort = '-created',
        filter = '',
        expand = 'category'
      } = options;

      console.log(`Service: Récupération de tous les articles - page: ${page}, perPage: ${perPage}`);

      const result = await pb.collection(this.collectionName).getList(page, perPage, {
        sort,
        filter,
        expand,
        requestKey: `articles-all-${page}-${Date.now()}` // Clé unique
      });

      console.log(`Service: ${result.items.length} articles récupérés sur ${result.totalItems} total`);
      return result;
    } catch (error) {
      // Ignorer les erreurs d'annulation automatique
      if (error.name === 'AbortError' || error.message?.includes('aborted')) {
        console.log('Service: Requête annulée pour tous les articles');
        return { items: [], totalItems: 0, page: 1, perPage: 0, totalPages: 0 };
      }
      
      console.error('Erreur lors de la récupération des articles:', error);
      throw error;
    }
  }

  /**
   * Récupérer un article par son ID
   * @param {string} id - ID de l'article
   * @param {string} expand - Relations à étendre
   * @returns {Promise<Object>} Article trouvé
   */
  static async getById(id, expand = 'category') {
    try {
      const article = await pb.collection(this.collectionName).getOne(id, {
        expand
      });
      return article;
    } catch (error) {
      console.error(`Erreur lors de la récupération de l'article ${id}:`, error);
      throw error;
    }
  }

  /**
   * Récupérer un article par son slug
   * @param {string} slug - Slug de l'article
   * @param {string} expand - Relations à étendre
   * @returns {Promise<Object>} Article trouvé
   */
  static async getBySlug(slug, expand = 'category') {
    try {
      const article = await pb.collection(this.collectionName).getFirstListItem(`slug="${slug}"`, {
        expand
      });
      return article;
    } catch (error) {
      console.error(`Erreur lors de la récupération de l'article avec le slug ${slug}:`, error);
      throw error;
    }
  }

  /**
   * Récupérer les articles par catégorie (supporte les catégories multiples)
   * @param {string} categoryId - ID de la catégorie
   * @param {Object} options - Options de requête
   * @returns {Promise<Object>} Liste des articles de la catégorie
   */
  static async getByCategory(categoryId, options = {}) {
    try {
      const {
        page = 1,
        perPage = 20,
        sort = '-created'
      } = options;

      console.log(`Service: Récupération des articles pour la catégorie ${categoryId}`);
      console.log(`Service: Paramètres - page: ${page}, perPage: ${perPage}, sort: ${sort}`);

      // Nouveau filtre pour supporter les catégories multiples
      // Fonctionne avec category en tant que relation unique OU tableau de relations
      const filter = `category ~ "${categoryId}"`;

      const result = await pb.collection(this.collectionName).getList(page, perPage, {
        sort,
        filter,
        expand: 'category',
        requestKey: `articles-category-${categoryId}-${page}` // Clé unique pour éviter les conflits
      });

      console.log(`Service: Articles récupérés pour la catégorie ${categoryId}:`, result.items.length);
      return result;
    } catch (error) {
      // Ignorer les erreurs d'annulation automatique
      if (error.name === 'AbortError' || error.message?.includes('aborted')) {
        console.log(`Service: Requête annulée pour la catégorie ${categoryId}`);
        return { items: [], totalItems: 0, page: 1, perPage: 0, totalPages: 0 };
      }
      
      console.error(`Erreur lors de la récupération des articles de la catégorie ${categoryId}:`, error);
      throw error;
    }
  }

  /**
   * Récupérer les articles par plusieurs catégories
   * @param {Array<string>} categoryIds - Tableau des IDs de catégories
   * @param {Object} options - Options de requête
   * @returns {Promise<Object>} Liste des articles des catégories
   */
  static async getByMultipleCategories(categoryIds, options = {}) {
    try {
      const {
        page = 1,
        perPage = 20,
        sort = '-created'
      } = options;

      console.log(`Service: Récupération des articles pour les catégories ${categoryIds.join(', ')}`);

      // Créer un filtre OR pour plusieurs catégories
      const categoryFilters = categoryIds.map(id => `category ~ "${id}"`).join(' || ');
      const filter = `(${categoryFilters})`;

      const result = await pb.collection(this.collectionName).getList(page, perPage, {
        sort,
        filter,
        expand: 'category',
        requestKey: `articles-categories-${categoryIds.join('-')}-${page}`
      });

      console.log(`Service: Articles récupérés pour les catégories multiples:`, result.items.length);
      return result;
    } catch (error) {
      if (error.name === 'AbortError' || error.message?.includes('aborted')) {
        console.log(`Service: Requête annulée pour les catégories multiples`);
        return { items: [], totalItems: 0, page: 1, perPage: 0, totalPages: 0 };
      }
      
      console.error(`Erreur lors de la récupération des articles pour les catégories multiples:`, error);
      throw error;
    }
  }

  /**
   * Rechercher des articles
   * @param {string} query - Terme de recherche
   * @param {Object} options - Options de requête
   * @returns {Promise<Object>} Résultats de recherche
   */
  static async search(query, options = {}) {
    try {
      const {
        page = 1,
        perPage = 20,
        sort = '-created'
      } = options;

      const filter = `title ~ "${query}" || content ~ "${query}" || excerpt ~ "${query}"`;

      const result = await pb.collection(this.collectionName).getList(page, perPage, {
        sort,
        filter,
        expand: 'category'
      });

      return result;
    } catch (error) {
      console.error('Erreur lors de la recherche d\'articles:', error);
      throw error;
    }
  }

  /**
   * Créer un nouvel article
   * @param {Object} data - Données de l'article
   * @returns {Promise<Object>} Article créé
   */
  static async create(data) {
    try {
      const article = await pb.collection(this.collectionName).create(data);
      return article;
    } catch (error) {
      console.error('Erreur lors de la création de l\'article:', error);
      throw error;
    }
  }

  /**
   * Mettre à jour un article
   * @param {string} id - ID de l'article
   * @param {Object} data - Nouvelles données
   * @returns {Promise<Object>} Article mis à jour
   */
  static async update(id, data) {
    try {
      const article = await pb.collection(this.collectionName).update(id, data);
      return article;
    } catch (error) {
      console.error(`Erreur lors de la mise à jour de l'article ${id}:`, error);
      throw error;
    }
  }

  /**
   * Supprimer un article
   * @param {string} id - ID de l'article
   * @returns {Promise<boolean>} Succès de la suppression
   */
  static async delete(id) {
    try {
      await pb.collection(this.collectionName).delete(id);
      return true;
    } catch (error) {
      console.error(`Erreur lors de la suppression de l'article ${id}:`, error);
      throw error;
    }
  }

  /**
   * Récupérer les articles récents
   * @param {number} limit - Nombre d'articles à récupérer
   * @returns {Promise<Array>} Articles récents
   */
  static async getRecent(limit = 5) {
    try {
      const result = await pb.collection(this.collectionName).getList(1, limit, {
        sort: '-created',
        expand: 'category'
      });
      return result.items;
    } catch (error) {
      console.error('Erreur lors de la récupération des articles récents:', error);
      throw error;
    }
  }

  /**
   * Récupérer les articles populaires (basé sur la date de mise à jour)
   * @param {number} limit - Nombre d'articles à récupérer
   * @returns {Promise<Array>} Articles populaires
   */
  static async getPopular(limit = 5) {
    try {
      const result = await pb.collection(this.collectionName).getList(1, limit, {
        sort: '-updated',
        expand: 'category'
      });
      return result.items;
    } catch (error) {
      console.error('Erreur lors de la récupération des articles populaires:', error);
      throw error;
    }
  }
}